import React, { useState, useMemo } from "react";
import { Tabs, Tab, Grid, Typography, Box } from "@mui/material";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import { Mimshak } from "../../../interfaces/DTO/mislaka_xml_models";
import { Contact } from "../../../interfaces/DTO/crm/contact";
import StyledDrawer from "../../../shared/UIParts/StyledDrawer";
import { FamilyInfo } from "../../Consulting/FamilyInfo";
import { ContactInfo } from "./ContactInfo";
import { EntityFilterCategory, formatEntityFilterCategory } from "../../../utils/featuresFormatter";

import { ContactCommentType } from "../../../interfaces/DTO/crm/contactComment";
import { ContactCommentsList } from "./ContactCommentsList";
import { ShareContact } from "../../Agent/Share/ShareContact";
import GeneratePublicLink from "../../Users/GeneratePublicLink";
import ContactCommentsPanel from "./ContactCommentsPanel";
import ContactPortalAccessCard, { FamilyInfoForCard } from "./ContactPortalAccessCard";

interface Props {
  open: boolean;
  onClose: () => void;
  viewedType: "USER" | "FAMILY";
  id: number;
  name: string;
  contacts: Contact[];
  mimshaks: Mimshak[];
  familyInfo?: FamilyInfoForCard;
  selectedPeriodType?: "REGULAR" | "PRE_ADVICE";
}

export const ContactDetailsDrawer: React.FC<Props> = ({
  open,
  onClose,
  viewedType,
  id,
  name,
  contacts,
  mimshaks,
  familyInfo,
  selectedPeriodType,
}) => {
  const [selectedTab, setSelectedTab] = useState(0);

  const tabs = useMemo(() => {
    const baseTabs = [];
    const contact = contacts[0];
    if (viewedType === "FAMILY") {
      baseTabs.push({
        label: "פרטי משפחה",
        content: <FamilyInfo family={{ id, name }} familyMemberContacts={contacts} />,
      });
      baseTabs.push({
        label: "פרטי לקוח",
        content: <ContactInfo contact={contacts.find((u) => u.id === id)!} mimshaks={mimshaks} />,
      });
    } else {
      baseTabs.push({
        label: "פרטי לקוח",
        content: <ContactInfo contact={contact} mimshaks={mimshaks} />,
      });
      baseTabs.push({
        label: "שיתוף",
        content: (
          <>
            <Grid xs={12}>
              <ContactPortalAccessCard contactId={contact.id!} familyInfo={familyInfo} selectedPeriodType={selectedPeriodType} />
            </Grid>
            <Grid xs={12}>
              <GeneratePublicLink contactId={contact.id!} />
            </Grid>
            <Grid xs={12}>
              <ShareContact contact={contact} />
            </Grid>
          </>
        ),
      });
    }
    baseTabs.push({
      label: <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><CommentOutlinedIcon fontSize="small" />הערות</Box>,
      content: (
        <ContactCommentsPanel
          contacts={contacts}
          isFamily={viewedType === "FAMILY"}
        />
      ),
    });
    baseTabs.push({
      label: formatEntityFilterCategory(EntityFilterCategory.HasQuestion),
      content: (
        <ContactCommentsList
          contactId={id}
          type={ContactCommentType.SMALLTALK}
        />
      ),
    });
    baseTabs.push({
      label: "משימות",
      content: <Typography variant="h6" sx={{ p: 2 }}>כאן תופיע רשימת משימות</Typography>,
    });
    baseTabs.push({
      label: "תשלומים",
      content: <Typography variant="h6" sx={{ p: 2 }}>כאן יופיעו פרטי תשלומים</Typography>,
    });

    return baseTabs;
  }, [viewedType, id, name, contacts, mimshaks, familyInfo, selectedPeriodType]);

  return (
    <StyledDrawer
      open={open}
      onClose={onClose}
      onBack={onClose}
      width={600}
      anchor="right"
    >
      <Tabs
        value={selectedTab}
        onChange={(_, newValue) => setSelectedTab(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          "& .MuiTab-root": {
            fontSize: "16px",
            textTransform: "none",
            minWidth: "100px",
          },
          "& .Mui-selected": {
            color: "#1976d2",
          },
        }}
      >
        {tabs.map((tab, index) => (
          <Tab key={index} label={tab.label} />
        ))}
      </Tabs>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        {tabs[selectedTab]?.content}
      </Grid>
    </StyledDrawer>
  );
};
